'use client';

/**
 * ArtifactViewer — full-size viewer for a `data-artifact` part, opened from
 * ArtifactCard. Controlled ResponsiveModal (`open`/`onOpenChange`, no
 * globalModalId — same pattern as ChatDetailsModal).
 *
 * The per-kind renderers live in ./ArtifactRenderers so the card can reuse them
 * for its inline previews; see that module for the security notes on html
 * sandboxing and image src validation, and `artifactInlinePreview` for which
 * kinds show in the flow instead of only here.
 *
 * The toolbar is per-kind, because "copy" and "download" do not mean the same
 * thing for every artifact:
 *   - table → Download CSV (Excel-ready) / JSON (round-trips) / open in its own
 *     window, which is the only way to keep a big table beside the conversation.
 *   - image → Download fetches the BYTES; Copy is hidden (copying a URL is not
 *     what "copy" means for a picture).
 *   - everything else → Copy + Download the text.
 */

import React from 'react';
import { ResponsiveModal } from '@west-tennessee-consulting/ui/components/responsive-modal';
import {
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  ExternalLinkIcon,
  TableIcon,
} from 'lucide-react';
import type { ArtifactData } from '../../lib/artifacts/types.js';
import { ArtifactContent, ARTIFACT_HTML_SANDBOX } from './ArtifactRenderers.js';
import {
  artifactFileName,
  artifactKindLabel,
  artifactSizeLabel,
  parseArtifactTable,
} from './artifact-utils.js';
import { toCsv } from './artifact-table-utils.js';
import { openTableInNewWindow } from './open-table-window.js';

export { ARTIFACT_HTML_SANDBOX };

const TOOLBAR_BUTTON_CLASSES =
  'inline-flex items-center gap-1.5 min-h-11 px-3 rounded-md border border-border bg-transparent text-sm font-medium text-foreground hover:bg-muted/60 transition-colors';

/** Hand the browser a file. One place, so the object-URL cleanup is not forgotten. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function slugForDownload(artifact: Pick<ArtifactData, 'title'>): string {
  return (
    artifact.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'table'
  );
}

function CopyButton({ content }: { content: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions/insecure context) — quiet no-op;
      // the user still has the download button.
    }
  };

  return (
    <button type="button" onClick={handleCopy} className={TOOLBAR_BUTTON_CLASSES} aria-label="Copy artifact content">
      {copied ? <CheckIcon className="size-4" aria-hidden /> : <CopyIcon className="size-4" aria-hidden />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function DownloadButton({ artifact }: { artifact: ArtifactData }) {
  const handleDownload = async () => {
    // An image artifact's `content` is a URL, not the file — downloading it
    // verbatim would save a text file containing a link. Fetch the bytes.
    const blob =
      artifact.kind === 'image'
        ? await fetch(artifact.content).then((response) => response.blob())
        : new Blob([artifact.content], { type: 'text/plain;charset=utf-8' });
    downloadBlob(blob, artifactFileName(artifact));
  };

  return (
    <button
      type="button"
      onClick={() => {
        void handleDownload();
      }}
      className={TOOLBAR_BUTTON_CLASSES}
      aria-label="Download artifact"
    >
      <DownloadIcon className="size-4" aria-hidden />
      Download
    </button>
  );
}

/**
 * Table toolbar. Falls back to the generic buttons when the content does not
 * parse — an unparseable "table" is really just text, and offering a CSV export
 * of it would produce a broken file.
 */
function TableToolbar({ artifact }: { artifact: ArtifactData }) {
  const [popupBlocked, setPopupBlocked] = React.useState(false);
  const parsed = React.useMemo(() => parseArtifactTable(artifact.content), [artifact.content]);

  if (!parsed) {
    return (
      <>
        <CopyButton content={artifact.content} />
        <DownloadButton artifact={artifact} />
      </>
    );
  }

  const slug = slugForDownload(artifact);

  return (
    <>
      {popupBlocked && (
        <span className="text-xs text-muted-foreground" role="status">
          Your browser blocked the new window — allow popups for Hub to use it.
        </span>
      )}
      <button
        type="button"
        onClick={() => {
          setPopupBlocked(openTableInNewWindow(artifact, parsed) === 'blocked');
        }}
        className={TOOLBAR_BUTTON_CLASSES}
        aria-label="Open table in a new window"
      >
        <ExternalLinkIcon className="size-4" aria-hidden />
        New window
      </button>
      <button
        type="button"
        onClick={() =>
          downloadBlob(
            new Blob([toCsv(parsed.columns, parsed.rows)], { type: 'text/csv;charset=utf-8' }),
            `${slug}.csv`,
          )
        }
        className={TOOLBAR_BUTTON_CLASSES}
        aria-label="Download table as CSV"
      >
        <TableIcon className="size-4" aria-hidden />
        CSV
      </button>
      <button
        type="button"
        onClick={() =>
          downloadBlob(
            new Blob([artifact.content], { type: 'application/json;charset=utf-8' }),
            `${slug}.json`,
          )
        }
        className={TOOLBAR_BUTTON_CLASSES}
        aria-label="Download table as JSON"
      >
        <DownloadIcon className="size-4" aria-hidden />
        JSON
      </button>
    </>
  );
}

function ArtifactToolbar({ artifact }: { artifact: ArtifactData }) {
  if (artifact.kind === 'table') return <TableToolbar artifact={artifact} />;
  return (
    <>
      {artifact.kind !== 'image' && <CopyButton content={artifact.content} />}
      <DownloadButton artifact={artifact} />
    </>
  );
}

export interface ArtifactViewerProps {
  artifact: ArtifactData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ArtifactViewer({ artifact, open, onOpenChange }: ArtifactViewerProps) {
  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={artifact.title}
      description={`${artifactKindLabel(artifact)} · ${artifactSizeLabel(artifact)}`}
      // A table needs the width more than anything else here.
      maxWidth={artifact.kind === 'table' ? '5xl' : '3xl'}
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <ArtifactToolbar artifact={artifact} />
        </div>
        <ArtifactContent artifact={artifact} />
      </div>
    </ResponsiveModal>
  );
}
